import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDealerAuthStore } from '@/store/dealerAuthStore';

interface Props {
  children: ReactNode;
  requireOwner?: boolean;
}

export function DealerRequireAuth({ children, requireOwner = false }: Props) {
  const location = useLocation();
  const { user, company, logout, loadMe } = useDealerAuthStore();

  useEffect(() => {
    if (!user) {
      void loadMe();
    }
  }, [user, loadMe]);

  const token = typeof window !== 'undefined' ? localStorage.getItem('dealerToken') : null;
  if (!token) {
    return <Navigate to="/dealer/login" state={{ from: location }} replace />;
  }

  if (user === null) {
    return <div className="py-16 text-center">Loading…</div>;
  }

  // Check company suspension
  if (company?.status === 'suspended') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Account Suspended</h2>
          <p className="text-gray-500 text-sm mb-6">
            Your dealer account has been suspended. Please contact support for more information.
          </p>
          <button
            onClick={() => void logout()}
            className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  // Check trial expiry
  const trialExpired =
    company?.trialExpiresAt != null && new Date(company.trialExpiresAt).getTime() < Date.now();

  if (trialExpired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Trial Expired</h2>
          <p className="text-gray-500 text-sm mb-6">
            Your free trial has ended. Please contact us to upgrade your plan and continue using the dealer portal.
          </p>
          <button
            onClick={() => void logout()}
            className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  if (requireOwner && user.role !== 'owner') {
    return <Navigate to="/dealer/dashboard" replace />;
  }

  return <>{children}</>;
}

export default DealerRequireAuth;
